import { USE_TRANSCRIPTION_SERVICE_V2 } from '@/components/constants/flags';
import { getDemoModeForSession } from '@/features/demo-flow/utils/getDemoModeForSession';
import { getDomain } from '@/helpers/getDomains';
import { getAuthTokenForHttpClient } from '@/utils/http/getAuthTokenForHttpClient';
import { buildScribeSseHeaders } from '@/utils/http/scribeSseHeaders';
import { logger } from '@/utils/logger/logger';
import { handleStreamOpen } from '@/utils/sseErrorHandler';
import { fetchEventSource } from '@fortaine/fetch-event-source';
import { sanitizeError } from '@heidi/runtime/logger';
import { type SideBySideConsultNoteGenerationRequest } from '@heidi/session/contracts';
import {
  PERSONALISATION_STREAM_CAPABILITY,
  streamSchema,
  tryHandleNoteGenerationPhaseEvent,
  tryHandleNoteSuggestionsEvent,
  type NoteGenerationPhaseFrame,
  type NoteSuggestionsFrame,
  type StreamType,
} from '@heidi/session/data';
type PersonalisationCapableSideBySideRequest = SideBySideConsultNoteGenerationRequest & {
  client_capabilities?: string[];
};

export interface StreamEventHandlers {
  onOpen?: (response: Response) => Promise<void> | void;
  onMessage?: (streamData: StreamType) => void;
  onNoteSuggestions?: (frame: NoteSuggestionsFrame) => void;
  onNoteGenerationPhase?: (frame: NoteGenerationPhaseFrame) => void;
  onClose?: () => void;
  onError?: (error: unknown) => void;
}

export interface StreamConfig {
  sessionId: string;
  consultNoteId: string;
  signal: AbortSignal;
  logPrefix?: string;
}

/**
 * Streams a consult note generation request and handles the response
 * @param config - Configuration for the streaming request (must include signal for abort control)
 * @param handlers - Event handlers for stream events
 */
export async function streamConsultNoteGenerationV2_2(
  config: StreamConfig,
  handlers: StreamEventHandlers = {}
): Promise<void> {
  const {
    sessionId,
    consultNoteId,
    signal,
    logPrefix = '[CONSULT_NOTE_STREAM]',
  } = config;

  const {
    onOpen,
    onMessage,
    onNoteSuggestions,
    onNoteGenerationPhase,
    onClose,
    onError,
  } = handlers;

  const token = await getAuthTokenForHttpClient();
  if (signal?.aborted) return;
  const requestHeaders = buildScribeSseHeaders(token);

  const reqBody: PersonalisationCapableSideBySideRequest = {
    consult_note_id: consultNoteId,
    ignore_backup: USE_TRANSCRIPTION_SERVICE_V2(),
    // Tag shortened-demo notes so the backend can separate them from real
    // generations in the consult_note_generation event.
    demo_mode: getDemoModeForSession(sessionId),
    // Only callers that provide reconciliation handling may opt into the
    // backend-owned authoritative personalisation protocol. SBS candidates do
    // not reconcile persisted notes and therefore remain on the legacy path.
    ...(onNoteGenerationPhase
      ? { client_capabilities: [PERSONALISATION_STREAM_CAPABILITY] }
      : {}),
  };

  return fetchEventSource(buildConsultNoteGenerationV2_2RequestPath(sessionId), {
    headers: requestHeaders,
    openWhenHidden: true,
    method: 'POST',
    body: JSON.stringify(reqBody),
    signal,
    onopen: (response) =>
      handleStreamOpen({
        response,
        logPrefix,
        onOpen,
        onError,
        context: { consultNoteId },
      }),
    onmessage(ev) {
      if (
        tryHandleNoteGenerationPhaseEvent(ev, onNoteGenerationPhase, logPrefix, {
          consultNoteId,
        })
      ) {
        return;
      }
      if (
        tryHandleNoteSuggestionsEvent(ev, onNoteSuggestions, logPrefix, {
          consultNoteId,
        })
      ) {
        return;
      }

      const message = ev.data;

      // Parse and validate the stream data using the schema
      const streamParseResult = streamSchema.safeParse(message);
      if (streamParseResult.error) {
        logger.error(`${logPrefix} stream parse error`, {
          error: streamParseResult.error,
          consultNoteId,
          rawMessage: message,
        });
        return;
      }

      try {
        onMessage?.(streamParseResult.data);
      } catch (error) {
        logger.error(`${logPrefix} onMessage handler error`, error);
      }
    },
    onclose() {
      logger.debug(`${logPrefix} stream closed`, { consultNoteId });
      try {
        onClose?.();
      } catch (error) {
        logger.error(`${logPrefix} onClose handler error`, error);
      }
    },
    onerror(err) {
      logger.error(`${logPrefix} stream error`, {
        errorName: err instanceof Error ? err.name : typeof err,
        error: sanitizeError(err),
        consultNoteId,
      });
      try {
        onError?.(err as unknown);
      } catch (error) {
        // The single-stream onError rethrows the stream error it just logged.
        if (error !== err) {
          logger.error(`${logPrefix} onError handler error`, {
            errorName: error instanceof Error ? error.name : typeof error,
            error: sanitizeError(error),
          });
        }
      }
      throw err; // throw error to stop the stream from retrying
    },
  });
}

export const buildConsultNoteGenerationV2_2RequestPath = (sessionId: string) => {
  return `${getDomain('v2-2')}sessions/${sessionId}/consult-note`;
};
